import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { whatsappLink } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { PlaneLanding, ClipboardCheck, Activity, PlaneTakeoff, Headset, MessageCircle, ShieldCheck, Info } from "lucide-react";

const DAY_ICONS = [PlaneLanding, ClipboardCheck, Activity, PlaneTakeoff];

export default function TripJourney() {
  const { openQuote } = useQuote();
  const { t } = useLang();
  const days = t("trip.days");
  return (
    <div>
      <section className="bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
          <div className="relative max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold"><Headset className="w-3.5 h-3.5" /> {t("trip.badge")}</span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl mt-4 leading-tight">{t("trip.title")}</h1>
            <p className="text-white/70 mt-4" dangerouslySetInnerHTML={{ __html: t("trip.sub") }} />
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <div className="relative">
          <div className="absolute left-6 top-2 bottom-2 w-0.5 bg-border hidden sm:block" />
          <div className="space-y-8">
            {days.map((d, i) => {
              const Icon = DAY_ICONS[i];
              return (
                <div key={i} className="relative flex gap-5 animate-fade-up" style={{ animationDelay: `${i * 90}ms` }} data-testid={`trip-day-${i + 1}`}>
                  <div className="shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center relative z-10 shadow-md">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 rounded-2xl border border-border bg-white p-6 shadow-sm">
                    <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{d.day}</p>
                    <h3 className="font-heading text-xl font-bold mt-1">{d.title}</h3>
                    <ul className="mt-3 space-y-2">
                      {d.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-foreground/80">
                          <ShieldCheck className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8 rounded-2xl bg-primary/5 border border-primary/10 p-5 flex items-start gap-3">
          <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            <b className="text-foreground">{t("trip.goodToKnow")}</b> {t("trip.goodToKnowRest")}
          </p>
        </div>

        <div className="mt-10 rounded-3xl bg-secondary text-white p-8 sm:p-10 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">{t("trip.ctaTitle")}</h2>
          <p className="text-white/70 mt-2 max-w-lg mx-auto">{t("trip.ctaSub")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <Button onClick={() => openQuote({ type: "quote" })} className="rounded-full h-12 px-8 bg-accent hover:bg-accent/90" data-testid="trip-get-quote-btn">{t("common.getFreeQuote")}</Button>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full h-12 px-8 w-full bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary" data-testid="trip-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-2" /> {t("common.talkToCoordinator")}</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
